import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { BookOpen, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n, LANG_LABELS } from '../i18n/I18nContext';
import { GUIDE, SECTIONS, TOUR } from '../help/guideContent';
import SpeakButton from './SpeakButton';

const OPEN_EVENT = 'kmt-open-tour';
const doneKey = (uid) => `kmt_tour_done_${uid}`;
export const openWelcomeTour = () => window.dispatchEvent(new Event(OPEN_EVENT));

// First-login walkthrough: a few role-specific slides, then a link to the full guide.
export default function WelcomeTour() {
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const slides = TOUR[user?.role] || [];

  useEffect(() => {
    if (user?.id && slides.length && !localStorage.getItem(doneKey(user.id))) setOpen(true);
    const onOpen = () => { setI(0); setOpen(true); };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !slides.length) return null;
  const close = () => { localStorage.setItem(doneKey(user.id), '1'); setOpen(false); };
  const guide = GUIDE[lang] || GUIDE.EN;
  const id = slides[i];
  const sec = SECTIONS.find((s) => s.id === id);
  const Icon = Icons[sec?.icon] || BookOpen;
  const last = i === slides.length - 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3" style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)' }} data-testid="welcome-tour">
      <div className="kmt-panel w-full max-w-lg p-6 relative kmt-fade-in" style={{ border: '1px solid var(--kmt-gold)' }}>
        <button className="absolute top-4 right-4" onClick={close} style={{ color: 'var(--kmt-muted)' }} aria-label="close" data-testid="welcome-tour-close"><X size={18} /></button>
        <div className="kmt-label" style={{ color: 'var(--kmt-gold)' }}>{i === 0 ? `${t('tour.welcome')}, ${user?.name || ''}` : `${t('tour.step')} ${i + 1} / ${slides.length}`}</div>
        <div className="flex gap-1.5 mt-3">
          {Object.keys(LANG_LABELS).map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`welcome-tour-lang-${l}`} className="px-2.5 py-1 rounded-md text-xs"
              style={lang === l ? { background: 'var(--kmt-gold)', color: '#111' } : { color: 'var(--kmt-muted)', border: '1px solid var(--kmt-border)' }}>{l === 'EN' ? 'English' : l === 'HI' ? 'हिन्दी' : 'ਪੰਜਾਬੀ'}</button>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(212,175,55,0.14)', color: 'var(--kmt-gold)' }}><Icon size={22} /></div>
          <h2 className="font-display font-bold text-lg flex-1 min-w-0" data-testid="welcome-tour-title">{guide[id].title}</h2>
          <SpeakButton key={`${id}-${lang}`} text={`${guide[id].title}. ${guide[id].steps.slice(0, 4).join(' ')}`} lang={lang} testid="welcome-tour-listen" />
        </div>
        <ul className="mt-4 space-y-2.5 max-h-[45vh] overflow-y-auto pr-1">
          {guide[id].steps.slice(0, 4).map((s, k) => (
            <li key={k} className="flex gap-2.5 text-sm leading-relaxed"><span style={{ color: 'var(--kmt-gold)' }}>•</span><span>{s}</span></li>
          ))}
        </ul>
        <div className="flex gap-1.5 justify-center mt-5">
          {slides.map((_, k) => <span key={k} className="h-1.5 rounded-full transition-all" style={{ width: k === i ? 20 : 6, background: k === i ? 'var(--kmt-gold)' : 'var(--kmt-border)' }} />)}
        </div>
        <div className="flex items-center justify-between gap-2 mt-5">
          <button className="text-xs" onClick={close} style={{ color: 'var(--kmt-muted)' }} data-testid="welcome-tour-skip">{t('tour.skip')}</button>
          <div className="flex gap-2">
            {i > 0 && <button className="kmt-btn-ghost flex items-center gap-1" onClick={() => setI(i - 1)} data-testid="welcome-tour-back"><ChevronLeft size={15} />{t('tour.back')}</button>}
            {last ? (
              <button className="kmt-btn-gold flex items-center gap-1.5" onClick={() => { close(); navigate('/help'); }} data-testid="welcome-tour-open-guide"><BookOpen size={15} />{t('tour.openGuide')}</button>
            ) : (
              <button className="kmt-btn-gold flex items-center gap-1" onClick={() => setI(i + 1)} data-testid="welcome-tour-next">{t('tour.next')}<ChevronRight size={15} /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
